import React, { useCallback, useEffect, useState } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { debugSupabaseConfigOnce } from '../lib/supabaseClient';
import { debugLog } from '../lib/debugLog';

const edgeBase = process.env.REACT_APP_SKINNY_SUPABASE_URL;

async function fetchIsConfigured() {
  try {
    const { data } = await import('../lib/supabaseClient');
    const supabase = data?.supabase || (await import('../lib/supabaseClient')).supabase;
    let tok = (await supabase.auth.getSession()).data.session?.access_token || '';
    for (let i = 0; !tok && i < 20; i++) { await new Promise(r=>setTimeout(r,150)); tok = (await supabase.auth.getSession()).data.session?.access_token || ''; }
    if (!tok || !edgeBase) return false;
    const res = await fetch(`${edgeBase}/functions/v1/settings-get`, { headers: { Authorization: `Bearer ${tok}` } });
    if (!res.ok) return false;
    const j = await res.json();
    return Boolean(j && typeof j.tenant_public_api_key === 'string' && j.tenant_public_api_key.length > 0);
  } catch (_) {
    return false;
  }
}

function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [oauthPending, setOauthPending] = useState(false);

  // Set robots noindex meta on Login to avoid indexing/scanner inflation
  useEffect(() => {
    try {
      let metaRobots = document.querySelector('meta[name="robots"]');
      if (!metaRobots) {
        metaRobots = document.createElement('meta');
        metaRobots.setAttribute('name', 'robots');
        document.head.appendChild(metaRobots);
      }
      // Preserve any existing directives and ensure noindex is present
      const prev = (metaRobots.getAttribute('content') || '').toLowerCase();
      const parts = new Set(prev.split(',').map(s => s.trim()).filter(Boolean));
      parts.add('noindex');
      metaRobots.setAttribute('content', Array.from(parts).join(','));
      return () => {
        // Best-effort cleanup: if we added noindex to an empty tag, remove it
        // (avoid removing if other pages also rely on this shared meta)
      };
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await import('../lib/supabaseClient');
      const supabase = data?.supabase || (await import('../lib/supabaseClient')).supabase;
      const sess = await supabase.auth.getSession();
      if (sess?.data?.session) {
        const configured = await fetchIsConfigured();
        navigate(configured ? '/dashboard' : '/getting-started', { replace: true });
      }
    })();
  }, [navigate]);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setPending(true);
    try {
      if (process.env.NODE_ENV === 'development') {
        debugLog('[Login] origin:', window.location.origin);
        debugSupabaseConfigOnce();
      }
      debugLog('[Login] submit start', { email });
      const res = await login(email, password);
      debugLog('[Login] submit result', { ok: res?.ok, error: res?.error });
      if (!res.ok) {
        const code = res?.error?.code || '';
        if (code === 'email_not_confirmed' || /confirm/i.test(res?.error?.message || '')) {
          alert('Please confirm your email first. We can resend the link.');
        }
        // Always log minimal env diagnostics to help spot misconfigured environments
        try {
          const u = process.env.REACT_APP_SKINNY_SUPABASE_URL;
          const k = process.env.REACT_APP_SKINNY_SUPABASE_ANON_KEY || '';
          // eslint-disable-next-line no-console
          console.warn('[Login][EnvCheck]', {
            url: u,
            anonPrefix: k ? k.slice(0, 4) : 'MISS',
            anonLen: k ? k.length : 0,
          });
        } catch (_) { /* noop */ }
        throw new Error(res?.error?.message || 'Login failed');
      }
      {
        const configured = await fetchIsConfigured();
        const nextPath = configured ? '/dashboard' : '/getting-started';
        navigate(nextPath);
        // Extended post-submit wait + URL check loop for deterministic automation
        for (let i = 0; i < 40; i++) {
          await new Promise(r => setTimeout(r, 150));
          try {
            if (window.location.pathname === nextPath) break;
            // Re-assert navigation a couple of times if needed
            if (i === 5 || i === 15 || i === 25) navigate(nextPath, { replace: true });
          } catch (_) { /* ignore */ }
        }
      }
    } catch (err) {
      setError(err.message || 'Error');
      debugLog('[Login] submit error', err);
      // Repeat minimal env diagnostics in catch as a fallback
      try {
        const u = process.env.REACT_APP_SKINNY_SUPABASE_URL;
        const k = process.env.REACT_APP_SKINNY_SUPABASE_ANON_KEY || '';
        // eslint-disable-next-line no-console
        console.warn('[Login][EnvCheck][catch]', {
          url: u,
          anonPrefix: k ? k.slice(0, 4) : 'MISS',
          anonLen: k ? k.length : 0,
        });
      } catch (_) { /* noop */ }
    } finally {
      setPending(false);
    }
  }, [email, password, login, navigate]);

  const loginWithGoogle = useCallback(async () => {
    setError('');
    setOauthPending(true);
    try {
      const { supabase } = await import('../lib/supabaseClient');
      const redirectTo = `${window.location.origin}/auth/callback?next=${encodeURIComponent('/dashboard')}`;
      await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          scopes: 'openid email profile'
        }
      });
      // Redirect will occur; no further code runs here in success path
    } catch (err) {
      setError(err.message || 'Google sign-in failed');
      setOauthPending(false);
    }
  }, []);

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={6} lg={5}>
          <h1 className="h3 mb-4">Log in</h1>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form onSubmit={submit}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={email} onChange={e => setEmail(e.target.value)} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control type="password" value={password} onChange={e => setPassword(e.target.value)} required />
            </Form.Group>
            <Button type="submit" variant="primary" disabled={pending}>Log in</Button>
          </Form>
          <div className="mt-3">
            <Button variant="outline-dark" onClick={loginWithGoogle} disabled={oauthPending} className="d-flex align-items-center justify-content-center gap-2">
              {oauthPending ? (
                'Redirecting…'
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    <path fill="none" d="M0 0h48v48H0z"/>
                  </svg>
                  Continue with Google
                </>
              )}
            </Button>
          </div>
          <div className="mt-3">
            No account? <Link to="/signup">Sign up</Link>
            <span className="ms-2">·</span>
            <Link className="ms-2" to="/forgot-password">Forgot password?</Link>
            <span className="ms-2">·</span>
            <span className="ms-1">Didn’t get the email? <Link to="/resendemail">Resend</Link></span>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default Login;


